import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { X, Calendar as CalendarIcon, Clock, Loader2 } from "lucide-react";
import { FaGoogle } from "react-icons/fa";
import { organizationService } from "../../services/api/organization";

type ModalType = "google" | "calendly" | null;

const SchedulingPage: React.FC = () => {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [searchParams] = useSearchParams();
  const orgId = searchParams.get("Id");
  const [googleLoading, setGoogleLoading] = useState(false);

  // Placeholder URLs - to be replaced with actual organization links
  const calendlyUrl = "https://calendly.com/25thandstaffing-msga";
  const googleCalendarUrl =
    "https://calendar.google.com/calendar/appointments/schedules/";

  const closeModal = () => setActiveModal(null);

  const handleGoogleClick = async () => {
    if (orgId) {
      setGoogleLoading(true);
      try {
        const response = await organizationService.getBookingLink(orgId);
        if (response?.data?.bookingLink || response?.bookingLink) {
          const url = response?.data?.bookingLink || response?.bookingLink;
          window.open(url, "_blank");
        } else {
          setActiveModal("google");
        }
      } catch (err) {
        console.error("Failed to fetch booking link", err);
        setActiveModal("google");
      } finally {
        setGoogleLoading(false);
      }
    } else {
      setActiveModal("google");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center p-4 selection:bg-primary/10 relative overflow-hidden font-abel">
      {/* Decorative Blobs */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-gradient-to-br from-blue-100 to-indigo-50 rounded-full blur-[80px] opacity-70 pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-gradient-to-tr from-rose-50 to-orange-50 rounded-full blur-[80px] opacity-70 pointer-events-none" />

      <div className="max-w-xl w-full">
        {/* Main Card */}
        <div className="bg-white/70 backdrop-blur-2xl border border-white/80 rounded-[2rem] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.08)] p-8 sm:p-12 relative animate-in slide-in-from-bottom-4 duration-700 z-10 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 shadow-sm mb-6 text-primary">
            <CalendarIcon size={28} />
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight mb-3">
            Schedule Training
          </h1>
          <p className="text-[15px] sm:text-[16px] text-gray-500 max-w-sm mx-auto leading-relaxed mb-10">
            Choose your preferred platform below to find a time that works best
            for you.
          </p>

          <div className="space-y-4">
            {/* <button
              onClick={handleGoogleClick}
              disabled={googleLoading}
              className="w-full flex items-center justify-between p-4 border border-gray-200/70 bg-white rounded-2xl hover:border-gray-300 hover:shadow-md transition-all duration-200 group active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center text-gray-700 border border-gray-100 group-hover:bg-white transition-colors">
                  {googleLoading ? (
                    <Loader2 className="animate-spin text-gray-400" size={24} />
                  ) : (
                    <FaGoogle className="text-2xl text-[#4285F4]" />
                  )}
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">
                    Schedule with Google
                  </h3>
                  <p className="text-sm text-gray-500">
                    Book via Google Calendar
                  </p>
                </div>
              </div>
              <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-gray-100 transition-colors">
                <Clock size={16} />
              </div>
            </button> */}

            <button
              onClick={() => setActiveModal("calendly")}
              className="w-full flex items-center justify-between p-4 border border-gray-200/70 bg-white rounded-2xl hover:border-gray-300 hover:shadow-md transition-all duration-200 group active:scale-[0.99]"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100 group-hover:bg-blue-100/50 transition-colors">
                  <span className="font-bold text-xl font-sans">C</span>
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">
                    Schedule with Calendly
                  </h3>
                  <p className="text-sm text-gray-500">
                    Book via Calendly widget
                  </p>
                </div>
              </div>
              <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-gray-100 transition-colors">
                <Clock size={16} />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Modal Overlay */}
      {activeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-4xl h-[85vh] sm:h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 relative">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">
              <div className="flex items-center gap-3">
                {activeModal === "google" ? (
                  <FaGoogle className="text-xl text-[#4285F4]" />
                ) : (
                  <div className="w-6 h-6 rounded-md bg-blue-50 flex items-center justify-center text-blue-600 font-bold font-sans text-sm">
                    C
                  </div>
                )}
                <h2 className="font-bold text-gray-900">
                  {activeModal === "google"
                    ? "Google Calendar Booking"
                    : "Calendly Booking"}
                </h2>
              </div>
              <button
                onClick={closeModal}
                className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body / Iframe */}
            <div className="flex-1 bg-gray-50 w-full relative">
              {/* Loader placeholder behind iframe */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
              </div>

              <iframe
                src={activeModal === "google" ? googleCalendarUrl : calendlyUrl}
                width="100%"
                height="100%"
                frameBorder="0"
                className="relative z-10 bg-white"
                title={
                  activeModal === "google"
                    ? "Google Scheduling"
                    : "Calendly Scheduling"
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchedulingPage;
